// Client-Pipeline fuer Bild-Uploads: Typ-Check, Downscale grosser Bilder, Upload.
// Serverseitige Validierung bleibt die Autoritaet — diese Checks sind nur UX.

import { api } from "../lib/api.js";

export const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
/** Spiegelt das serverseitige Limit (config storage.max_image_bytes). Nur UX. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
/** Lange Kante, auf die sehr grosse Bilder vor dem Upload herunterskaliert werden. */
export const MAX_UPLOAD_EDGE = 2560;
/** Lange Kante der Platzierung auf dem Canvas (Original bleibt im Store erhalten). */
export const MAX_DISPLAY_EDGE = 480;

export type ImageUploadErrorCode = "type" | "tooLarge" | "failed";

export class ImageUploadError extends Error {
  constructor(
    public readonly code: ImageUploadErrorCode,
    public override readonly cause?: unknown,
  ) {
    super(code);
    this.name = "ImageUploadError";
  }
}

export interface UploadResult {
  assetId: string;
  width: number;
  height: number;
}

export function isAcceptedImageType(type: string): boolean {
  return (ACCEPTED_IMAGE_TYPES as readonly string[]).includes(type);
}

/** Skaliert Dimensionen unter eine maximale Kantenlaenge, Seitenverhaeltnis bleibt erhalten. */
export function fitDisplaySize(
  width: number,
  height: number,
  maxEdge = MAX_DISPLAY_EDGE,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  const scale = longest > maxEdge ? maxEdge / longest : 1;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Skaliert ein Bild via Canvas herunter, falls die lange Kante MAX_UPLOAD_EDGE ueberschreitet. */
export async function downscaleImage(file: Blob, maxEdge = MAX_UPLOAD_EDGE): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const longest = Math.max(bitmap.width, bitmap.height);
    if (longest <= maxEdge) return file;
    const scale = maxEdge / longest;
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    const outType = isAcceptedImageType(file.type) ? file.type : "image/png";
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, outType, 0.92));
    return blob ?? file;
  } finally {
    bitmap.close();
  }
}

export async function uploadImage(boardId: string, file: File): Promise<UploadResult> {
  if (!isAcceptedImageType(file.type)) throw new ImageUploadError("type");
  let payload: Blob = file;
  try {
    payload = await downscaleImage(file);
  } catch {
    payload = file;
  }
  if (payload.size > MAX_IMAGE_BYTES) throw new ImageUploadError("tooLarge");
  const form = new FormData();
  form.append("file", payload, file.name || "image");
  try {
    return await api.upload<UploadResult>(`/api/boards/${boardId}/assets`, form);
  } catch (err) {
    throw new ImageUploadError("failed", err);
  }
}

export function imageFilesFromClipboard(data: DataTransfer | null): File[] {
  if (!data) return [];
  const out: File[] = [];
  for (const item of Array.from(data.items)) {
    if (item.kind === "file" && item.type.startsWith("image/")) {
      const file = item.getAsFile();
      if (file) out.push(file);
    }
  }
  return out;
}

export function imageFilesFromDrop(data: DataTransfer | null): File[] {
  if (!data) return [];
  return Array.from(data.files).filter((f) => f.type.startsWith("image/"));
}
